/**
 * A CONFERENCE'S OWN SIGN-IN SCREEN, AS PUBLISHED DATA (step 1, 2026-10-13).
 *
 * WHY. The owner, 2026-10-13: "go for the conference-supplied sign-in
 * screen". Until that day the words and the pictures a conference puts on
 * the platform's sign-in screen were constants in the code
 * (lib/workspaces/flagship-apps.ts `projects`): LA Tech Week and SF Tech
 * Week each had a translation key for the heading and the line and three
 * files under `public/images/sign-in/`, and the next conference needed a
 * deploy to get a screen. The design (docs/internal/architecture/
 * app-sign-in-and-onboarding.md, sections 4.1 to 4.6) always meant those
 * constants as the fallback of an owner-written setting. Step 1 makes the
 * conference SUPPLY them as part of the data it is published with: the
 * operator's catalog publisher (scripts/revent-catalog.ts `--sign-in <dir>`)
 * re-encodes the pictures and writes them, with the words, into the
 * conference's own project folder, and the portal reads them from there.
 * The constants stay, as the fallback for a conference with no published
 * screen and as the label and the partner table no data carries.
 *
 * TWO WRITERS. The operator's (step 1): a screen read from a folder the
 * catalog publisher itself wrote, the folder on the id the publisher derives
 * from the edition id its own stamp names (`publisherFolderOfEdition`; SF Tech
 * Week's hand-written constant for `sf-2026`), stamped with the conference
 * type, and public. Content ids are minted by the server at random, so no
 * user can put a row on one of those ids. And an organizer's (step 2,
 * 2026-10-13): a conference somebody made in Revent, read only when it is
 * published and shared by link, from files its owner wrote, under the
 * stricter `parseOrganizerSignInDoc` and the text rules of
 * lib/auth/sign-in-text-rules.ts; lib/apps/revent-sign-in-screens.ts says
 * what else is checked. The organizer's picture is re-encoded by the portal
 * before it is served (lib/auth/sign-in-image-encode.ts).
 *
 * WHAT THIS MODULE IS. The shapes and the rules both sides share, and
 * nothing that reads a database: the publisher validates with it before it
 * writes, the server lookup (lib/apps/revent-sign-in-screens.ts) validates
 * again with it after it reads, and the sign-in screen
 * (components/auth/login-dialog-body.tsx) validates a third time what the
 * page or the JSON route handed it. Plain module, client-safe, relative
 * imports only (it sits beside lib/workspaces/flagship-apps.ts in the
 * client and in the proxy's graph).
 *
 * THE WORDS ARE PLAIN TEXT. A heading of at most 60 code points, a line of at
 * most 160 in at most three written rows, a card's alt of at most 120.
 * Control characters, invisible characters and the ones that turn a text's
 * direction around are stripped, runs of spaces collapse, and nothing is
 * interpolated or parsed: the screen draws them as text nodes. The heading
 * and the line may be written once (every language reads the same words) or
 * once per language the platform speaks, English required, so a conference
 * whose words equal the constants looks exactly as it does today in all
 * fifteen languages. The publisher REFUSES text over a limit; a reader
 * CLAMPS it, as a seat belt for a row edited by hand.
 *
 * THE PICTURES ARE NEVER A URL FROM ANYWHERE. A published screen names each
 * picture by the first sixteen hex digits of its SHA-256 and nothing else;
 * the path is built here, from the folder id the platform validated, the
 * slot and that digest (`signInImagePath`), and always points at our own
 * route, which serves bytes the publisher re-encoded and checks them again
 * on the way out (app/api/auth/sign-in-screen/[conference]/[slot]/route.ts).
 * A value that is not exactly sixteen hex digits drops the picture.
 */

import { conferenceIds, PUBLISHED_ID_RE } from '../../public/widgets/revent/lib/edition-ids.js';
import { REVENT_SF_2026_PROJECT, type FlagshipSignInImage } from '../workspaces/flagship-apps';
import { signInWordsRefusals } from './sign-in-text-rules';

/** The longest heading, line and card alt, in code points (design section 4.1). */
export const SIGN_IN_HEADING_MAX = 60;
export const SIGN_IN_LINE_MAX = 160;
export const SIGN_IN_ALT_MAX = 120;
/** A line may put each of its short sentences on its own row; no more than this many. */
export const SIGN_IN_LINE_ROWS_MAX = 3;

/**
 * The languages a screen's words may be written in: the platform's own
 * (lib/i18n `supportedLocales`; a literal here because this module ships to
 * the proxy, which must not carry fifteen translation tables, and
 * tests/unit/auth/published-sign-in.test.ts holds the two equal).
 */
export const SIGN_IN_LOCALES = ['en', 'ar', 'de', 'es', 'fr', 'hi', 'id', 'it', 'ja', 'ko', 'nl', 'pt', 'uk', 'vi', 'zh'] as const;
export type SignInLocale = (typeof SIGN_IN_LOCALES)[number];
const LOCALE_SET: ReadonlySet<string> = new Set(SIGN_IN_LOCALES);

/** Words in one or more languages; English is always there and is the fallback. */
export type SignInText = { readonly en: string } & Readonly<Partial<Record<SignInLocale, string>>>;

/** The project folder's type, as the catalog publisher stamps it (scripts/revent-catalog.ts `MIME_CONF`; a test holds the two equal). */
export const REVENT_CONFERENCE_MIME = 'application/vnd.robutler.revent+json';

/** The three pictures: the band on a phone, the right half on a desktop, the link's preview card. */
export const SIGN_IN_SLOTS = ['phone', 'desktop', 'card'] as const;
export type SignInSlot = (typeof SIGN_IN_SLOTS)[number];
export const SIGN_IN_IMAGE_TYPES = ['image/webp', 'image/png', 'image/jpeg'] as const;
export type SignInImageType = (typeof SIGN_IN_IMAGE_TYPES)[number];

/** The card's one shape, the one every link-preview reader accepts. */
export const SIGN_IN_CARD_WIDTH = 1200;
export const SIGN_IN_CARD_HEIGHT = 630;
/** The band's pictures are square, and no smaller or larger than this a side. */
export const SIGN_IN_ART_MIN_PX = 256;
export const SIGN_IN_ART_MAX_PX = 2048;
/**
 * The most an encoded picture may weigh. Each picture is stored inline in its
 * own document (base64 in a JSON body), and the publisher writes inline rows
 * only (scripts/revent-catalog.ts `INLINE_MAX_BYTES`, 256 KiB: a blob written
 * from an operator's laptop lands on the laptop's disk). 180,000 bytes is
 * 240,000 characters of base64 with room for the envelope.
 */
export const SIGN_IN_IMAGE_MAX_BYTES = 180_000;

/** The document version this module reads and the publisher writes. */
export const SIGN_IN_DOC_VERSION = 1;
export const SIGN_IN_DOC_NAME = 'sign-in.json';
export const signInImageDocName = (slot: SignInSlot): string => `sign-in.${slot}.json`;

/** The JSON route the screen asks, and the stem of the picture route under it. */
export const SIGN_IN_SCREEN_ROUTE = '/api/auth/sign-in-screen';

const FOLDER_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/* A folder the publisher writes ends in `-9000-000000000001` (SF's constant
   and every derived id; public/widgets/revent/lib/edition-ids.js). */
const PUBLISHER_FOLDER_RE = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-9000-)000000000001$/;
const EDITION_RE = /^[a-z]{2,4}-[0-9]{4}$/;
const DIGEST_RE = /^[0-9a-f]{64}$/;
const V_RE = /^[0-9a-f]{16}$/;
const GROUND_RE = /^#[0-9a-f]{6}$/;

/** Is this a conference folder id (a lower-case uuid)? */
export function isConferenceFolderId(value: unknown): value is string {
  return typeof value === 'string' && FOLDER_RE.test(value);
}

/**
 * The ids of a screen's documents inside a publisher's folder: the screen
 * (`doc`) and one per picture, beside the catalog's own (folder `...0001`,
 * meta `...0002`, shards `...01xx`) on the same stem, in the `...03xx`
 * block. Null for any folder the publisher does not write on.
 */
export function signInDocIds(folderId: unknown): { doc: string } & Record<SignInSlot, string> | null {
  if (typeof folderId !== 'string') return null;
  const m = PUBLISHER_FOLDER_RE.exec(folderId);
  if (!m) return null;
  const stem = m[1];
  return {
    doc: `${stem}000000000300`,
    phone: `${stem}000000000301`,
    desktop: `${stem}000000000302`,
    card: `${stem}000000000303`,
  };
}

/** The folder the catalog publisher writes an edition into: SF Tech Week's constant, or the derived id. */
export function publisherFolderOfEdition(edition: unknown): string | null {
  if (typeof edition !== 'string' || !EDITION_RE.test(edition)) return null;
  if (edition === 'sf-2026') return REVENT_SF_2026_PROJECT;
  if (!PUBLISHED_ID_RE.test(edition)) return null;
  const ids = conferenceIds(edition) as { folderId: string } | null;
  return ids ? ids.folderId : null;
}

/**
 * Is this folder row one the catalog publisher wrote, for the edition its own
 * stamp names, on the id that edition derives? See the module header.
 */
export function isPublisherConferenceFolder(
  folderId: string,
  row: { contentType?: unknown; metadata?: unknown } | null | undefined,
): boolean {
  if (!row || row.contentType !== 'folder' || !isConferenceFolderId(folderId)) return false;
  const meta = row.metadata && typeof row.metadata === 'object' ? (row.metadata as Record<string, unknown>) : null;
  if (!meta || meta.projectMime !== REVENT_CONFERENCE_MIME) return false;
  const stamp = meta.reventCatalog && typeof meta.reventCatalog === 'object' ? (meta.reventCatalog as Record<string, unknown>) : null;
  return !!stamp && publisherFolderOfEdition(stamp.edition) === folderId;
}

/** Where the screen draws a picture from: our own route, built from validated parts only. */
export function signInImagePath(folderId: string, slot: SignInSlot, v: string): string | null {
  if (!isConferenceFolderId(folderId) || !(SIGN_IN_SLOTS as readonly string[]).includes(slot) || !V_RE.test(v)) return null;
  return `${SIGN_IN_SCREEN_ROUTE}/${folderId}/${slot}?v=${v}`;
}

/* ── the words ──────────────────────────────────────────────────────────── */

const ch = (n: number) => String.fromCharCode(n);
const span = (a: number, b: number) => `${ch(a)}-${ch(b)}`;
/* Controls (all but the line feed, which a line may use), the soft hyphen,
   the zero-width and direction marks, the line and paragraph separators,
   the direction embeddings and overrides, the invisible operators and
   direction isolates, and the byte order mark. Built from code points: a
   literal of these in source is itself a hazard. */
const STRIP_RE = new RegExp(
  `[${span(0x00, 0x08)}${span(0x0b, 0x1f)}${span(0x7f, 0x9f)}${ch(0xad)}${span(0x200b, 0x200f)}${span(0x2028, 0x202e)}${span(0x2060, 0x206f)}${ch(0xfeff)}]`,
  'g',
);

/**
 * Words as a screen may draw them: plain text, stripped and collapsed, rows
 * written with a line feed kept (at most `rows` of them; `rows: 1` joins
 * them), cut at `max` code points. `over` says the input broke a limit,
 * which the publisher refuses and a reader accepts cut. Null for no words.
 */
export function cleanSignInText(raw: unknown, max: number, rows = 1): { text: string; over: boolean } | null {
  if (typeof raw !== 'string') return null;
  const parts = raw
    .normalize('NFC')
    .replace(STRIP_RE, '')
    .split('\n')
    .map((row) => row.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean);
  if (!parts.length) return null;
  let over = parts.length > rows;
  const kept = rows > 1 ? [...parts.slice(0, rows - 1), parts.slice(rows - 1).join(' ')].filter(Boolean) : [parts.join(' ')];
  let text = kept.join('\n');
  const points = Array.from(text);
  if (points.length > max) {
    over = true;
    text = points.slice(0, max).join('').trimEnd();
  }
  return { text, over };
}

/**
 * Words in one language for all, or one per language (English required).
 * `strict` (the publisher) reports every problem; a reader drops an unknown
 * language and keeps the words cut.
 */
export function cleanSignInWords(
  raw: unknown,
  max: number,
  rows: number,
  strict: boolean,
): { value: SignInText | null; problems: string[] } {
  const problems: string[] = [];
  const out: Partial<Record<SignInLocale, string>> = {};
  const take = (locale: string, value: unknown) => {
    const cleaned = cleanSignInText(value, max, rows);
    if (!cleaned) {
      problems.push(`${locale}: empty or not text`);
      return;
    }
    if (cleaned.over) problems.push(`${locale}: longer than ${max} characters or ${rows} rows`);
    out[locale as SignInLocale] = cleaned.text;
  };
  if (typeof raw === 'string') take('en', raw);
  else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [locale, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!LOCALE_SET.has(locale)) {
        problems.push(`${locale}: not a language the platform speaks`);
        continue;
      }
      take(locale, value);
    }
  } else problems.push('not text');
  if (!out.en) {
    problems.push('en: required');
    return { value: null, problems };
  }
  if (strict && problems.length) return { value: null, problems };
  return { value: out as SignInText, problems };
}

/** The words for a language, else the English. */
export function pickSignInText(text: SignInText, locale: unknown): string {
  return typeof locale === 'string' && LOCALE_SET.has(locale) ? (text[locale as SignInLocale] ?? text.en) : text.en;
}

/* ── the stored document ────────────────────────────────────────────────── */

/** One picture as the screen document records it. */
export type SignInImageRef = { sha: string; type: SignInImageType; w: number; h: number; bytes: number };

/** How the band draws the two pictures: whole on their own ground, or filling it. */
export type SignInArtFit =
  | { fit: 'whole'; ground: string; maxPx: number; artHeight: number | { phone: number; desktop: number } }
  | { fit: 'fill' };

/** `sign-in.json`, the screen document the publisher writes into the conference folder. */
export type SignInDoc = {
  v: typeof SIGN_IN_DOC_VERSION;
  edition: string;
  heading: SignInText;
  line: SignInText;
  image: (SignInArtFit & { phone: SignInImageRef; desktop: SignInImageRef }) | null;
  card: (SignInImageRef & { alt: string }) | null;
};

/** `sign-in.<slot>.json`, one picture's bytes in base64 and what they are. */
export type SignInImageDoc = SignInImageRef & { v: typeof SIGN_IN_DOC_VERSION; slot: SignInSlot; b64: string };

const isInt = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isShare = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 1;

/* `anyArtType`: an organizer's band picture may be PNG, JPEG or WebP, since
   the portal re-encodes it before serving; the publisher's is WebP already. */
function imageRefOf(raw: unknown, slot: SignInSlot, anyArtType = false): SignInImageRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.sha !== 'string' || !DIGEST_RE.test(r.sha)) return null;
  if (!(SIGN_IN_IMAGE_TYPES as readonly unknown[]).includes(r.type)) return null;
  if (!isInt(r.bytes, 1, SIGN_IN_IMAGE_MAX_BYTES)) return null;
  if (slot === 'card') {
    if (r.w !== SIGN_IN_CARD_WIDTH || r.h !== SIGN_IN_CARD_HEIGHT || r.type === 'image/webp') return null;
  } else if ((!anyArtType && r.type !== 'image/webp') || !isInt(r.w, SIGN_IN_ART_MIN_PX, SIGN_IN_ART_MAX_PX) || r.h !== r.w) return null;
  return { sha: r.sha, type: r.type as SignInImageType, w: r.w as number, h: r.h as number, bytes: r.bytes };
}

/** The band's presentation, validated; null for anything else. */
export function artFitOf(raw: unknown): SignInArtFit | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.fit === 'fill') return { fit: 'fill' };
  if (r.fit !== 'whole') return null;
  if (typeof r.ground !== 'string' || !GROUND_RE.test(r.ground) || !isInt(r.maxPx, 128, SIGN_IN_ART_MAX_PX)) return null;
  const share = r.artHeight;
  if (isShare(share)) return { fit: 'whole', ground: r.ground, maxPx: r.maxPx, artHeight: share };
  if (share && typeof share === 'object') {
    const s = share as Record<string, unknown>;
    if (isShare(s.phone) && isShare(s.desktop)) return { fit: 'whole', ground: r.ground, maxPx: r.maxPx, artHeight: { phone: s.phone, desktop: s.desktop } };
  }
  return null;
}

/**
 * A stored screen document, validated as a reader must: the version, the
 * edition, the words (cut to their limits), and each picture's record. A
 * picture or a card that does not validate is dropped on its own; words
 * that do not validate drop the whole screen.
 */
export function parseSignInDoc(raw: unknown): SignInDoc | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== SIGN_IN_DOC_VERSION || typeof r.edition !== 'string' || !EDITION_RE.test(r.edition)) return null;
  const heading = cleanSignInWords(r.heading, SIGN_IN_HEADING_MAX, 1, false).value;
  const line = cleanSignInWords(r.line, SIGN_IN_LINE_MAX, SIGN_IN_LINE_ROWS_MAX, false).value;
  if (!heading || !line) return null;
  let image: SignInDoc['image'] = null;
  if (r.image && typeof r.image === 'object') {
    const i = r.image as Record<string, unknown>;
    const fit = artFitOf(i);
    const phone = imageRefOf(i.phone, 'phone');
    const desktop = imageRefOf(i.desktop, 'desktop');
    if (fit && phone && desktop) image = { ...fit, phone, desktop };
  }
  let card: SignInDoc['card'] = null;
  if (r.card && typeof r.card === 'object') {
    const ref = imageRefOf(r.card, 'card');
    const alt = cleanSignInText((r.card as Record<string, unknown>).alt, SIGN_IN_ALT_MAX, 1);
    if (ref && alt) card = { ...ref, alt: alt.text };
  }
  return { v: SIGN_IN_DOC_VERSION, edition: r.edition, heading, line, image, card };
}

/**
 * An ORGANIZER's screen document (step 2, 2026-10-13), validated as the
 * reader must for words a person wrote on a conference they made: stricter
 * than `parseSignInDoc` on every count, because nothing an app wrote is
 * trusted and a refused screen falls back to the app's default rather than
 * showing part of one.
 *   - `edition` is the conference's own folder id (a made conference has no
 *     edition name), and must be the folder it was read from.
 *   - The words are REFUSED over a limit, not cut, and in a language the
 *     platform does not speak; and refused when any language breaks a text
 *     rule (lib/auth/sign-in-text-rules.ts).
 *   - The picture, when the document names one, is a PNG, JPEG or WebP
 *     square of 256 to 2048 px of at most 180,000 bytes, named by its
 *     SHA-256 for both slots (the portal re-encodes it to WebP before it is
 *     served); one that does not validate refuses the screen.
 *   - Never a card: whatever the document says, the link's preview keeps the
 *     app's default picture and words.
 */
export function parseOrganizerSignInDoc(raw: unknown, folderId: string): SignInDoc | null {
  if (!isConferenceFolderId(folderId) || !raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== SIGN_IN_DOC_VERSION || r.edition !== folderId) return null;
  const heading = cleanSignInWords(r.heading, SIGN_IN_HEADING_MAX, 1, true).value;
  const line = cleanSignInWords(r.line, SIGN_IN_LINE_MAX, SIGN_IN_LINE_ROWS_MAX, true).value;
  if (!heading || !line) return null;
  if (signInWordsRefusals(heading).length || signInWordsRefusals(line).length) return null;
  let image: SignInDoc['image'] = null;
  if (r.image !== undefined && r.image !== null) {
    if (typeof r.image !== 'object' || Array.isArray(r.image)) return null;
    const i = r.image as Record<string, unknown>;
    const fit = artFitOf(i);
    const phone = imageRefOf(i.phone, 'phone', true);
    const desktop = imageRefOf(i.desktop, 'desktop', true);
    if (!fit || !phone || !desktop) return null;
    image = { ...fit, phone, desktop };
  }
  return { v: SIGN_IN_DOC_VERSION, edition: folderId, heading, line, image, card: null };
}

/* ── what the screen is handed ──────────────────────────────────────────── */

/**
 * The screen as the page and the JSON route hand it to the browser: the
 * words, the presentation, and each picture's digest prefix. No folder id
 * (the browser asked for one and knows it), no path, no URL, no bytes.
 */
export type SignInScreenWire = {
  heading: SignInText;
  line: SignInText;
  image: (SignInArtFit & { phone: string; desktop: string }) | null;
  card: { v: string; alt: string } | null;
};

/** The wire shape of a stored document. */
export function wireOfSignInDoc(doc: SignInDoc): SignInScreenWire {
  const v = (ref: SignInImageRef) => ref.sha.slice(0, 16);
  const image = doc.image
    ? doc.image.fit === 'whole'
      ? { fit: 'whole' as const, ground: doc.image.ground, maxPx: doc.image.maxPx, artHeight: doc.image.artHeight, phone: v(doc.image.phone), desktop: v(doc.image.desktop) }
      : { fit: 'fill' as const, phone: v(doc.image.phone), desktop: v(doc.image.desktop) }
    : null;
  return { heading: doc.heading, line: doc.line, image, card: doc.card ? { v: v(doc.card), alt: doc.card.alt } : null };
}

/** A conference's published screen, ready to draw: pictures as our own paths. */
export type PublishedSignInScreen = {
  conference: string;
  heading: SignInText;
  line: SignInText;
  image: FlagshipSignInImage | null;
  card: { image: string; width: typeof SIGN_IN_CARD_WIDTH; height: typeof SIGN_IN_CARD_HEIGHT; alt: string } | null;
};

/**
 * The screen of `folderId` from what a page or the JSON route handed over,
 * validated again (the browser trusts nothing it is handed), with every
 * picture's path built here. Null for anything that is not a screen.
 */
export function screenFromWire(folderId: unknown, raw: unknown): PublishedSignInScreen | null {
  if (!isConferenceFolderId(folderId) || !raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const heading = cleanSignInWords(r.heading, SIGN_IN_HEADING_MAX, 1, false).value;
  const line = cleanSignInWords(r.line, SIGN_IN_LINE_MAX, SIGN_IN_LINE_ROWS_MAX, false).value;
  if (!heading || !line) return null;
  let image: FlagshipSignInImage | null = null;
  if (r.image && typeof r.image === 'object') {
    const i = r.image as Record<string, unknown>;
    const fit = artFitOf(i);
    const phone = typeof i.phone === 'string' ? signInImagePath(folderId, 'phone', i.phone) : null;
    const desktop = typeof i.desktop === 'string' ? signInImagePath(folderId, 'desktop', i.desktop) : null;
    if (fit && phone && desktop) {
      image =
        fit.fit === 'whole'
          ? { phone, desktop, whole: { ground: fit.ground, maxPx: fit.maxPx, artHeight: fit.artHeight } }
          : { phone, desktop, fill: true };
    }
  }
  let card: PublishedSignInScreen['card'] = null;
  if (r.card && typeof r.card === 'object') {
    const c = r.card as Record<string, unknown>;
    const path = typeof c.v === 'string' ? signInImagePath(folderId, 'card', c.v) : null;
    const alt = cleanSignInText(c.alt, SIGN_IN_ALT_MAX, 1);
    if (path && alt) card = { image: path, width: SIGN_IN_CARD_WIDTH, height: SIGN_IN_CARD_HEIGHT, alt: alt.text };
  }
  return { conference: folderId, heading, line, image, card };
}

/* ── the bytes ──────────────────────────────────────────────────────────── */

/**
 * What the first bytes say a picture is: PNG, JPEG or WebP, else null (SVG,
 * GIF, HTML and everything else). The type is judged here and nowhere else;
 * a name or a declared type is never asked.
 */
export function signInImageTypeOf(bytes: Uint8Array): SignInImageType | null {
  const b = bytes;
  if (b.length < 12) return null;
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return 'image/png';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'image/webp';
  return null;
}

/**
 * Does a PNG or WebP carry an animation (APNG's `acTL` before the first
 * image data, WebP's animation flag or an `ANIM`/`ANMF` chunk)? The
 * publisher refuses one: a picture on the sign-in screen is one frame.
 */
export function signInImageAnimated(bytes: Uint8Array): boolean {
  const b = bytes;
  const tag = (at: number) => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);
  const type = signInImageTypeOf(b);
  if (type === 'image/png') {
    let at = 8;
    while (at + 8 <= b.length) {
      const len = ((b[at] << 24) | (b[at + 1] << 16) | (b[at + 2] << 8) | b[at + 3]) >>> 0;
      const t = tag(at + 4);
      if (t === 'acTL') return true;
      if (t === 'IDAT' || t === 'IEND') return false;
      at += 12 + len;
    }
    return false;
  }
  if (type === 'image/webp') {
    if (b.length > 20 && tag(12) === 'VP8X' && (b[20] & 0x02) !== 0) return true;
    let at = 12;
    while (at + 8 <= b.length) {
      const t = tag(at);
      const size = (b[at + 4] | (b[at + 5] << 8) | (b[at + 6] << 16) | (b[at + 7] << 24)) >>> 0;
      if (t === 'ANIM' || t === 'ANMF') return true;
      at += 8 + size + (size & 1);
    }
  }
  return false;
}
